/**
 * File: src/components/admin-dynamic-json-tabs.js
 * Mô tả: Component tự động tạo Tab & Sub-Tab đa cấp theo đúng cấu trúc nhóm và tên trường (Key) gốc trong file JSON.
 * Liên quan: src/utils/json-tree-builder.js, src/components/admin-config-editor.js
 */

import { createElement } from '../utils/dom.js';
import { getNestedValue, setNestedValue } from '../utils/json-tree-builder.js';

/**
 * Render giao diện Tab nhiều cấp cho bất kỳ object JSON nào mà không đổi/dịch tên trường
 * @param {Object} jsonObj 
 * @param {Function} onChange 
 * @returns {HTMLElement}
 */
export const renderDynamicJsonTabs = (jsonObj, onChange) => {
  const container = createElement('div', { className: 'dynamic-json-tabs' });

  // Lấy danh sách các key cấp 1
  const topKeys = Object.keys(jsonObj);
  let activeTopKey = topKeys[0] || '';
  let activeSubKey = '';

  const renderUI = () => {
    container.innerHTML = '';
    if (!topKeys.length) {
      container.innerHTML = `<div style="padding: 1rem; color: var(--color-text-muted);">File JSON trống.</div>`;
      return;
    }

    // Nav Tab Cấp 1 (Top-Level Keys)
    const topNav = createElement('div', {
      className: 'top-key-nav',
      attributes: { style: 'display: flex; gap: 0.5rem; flex-wrap: wrap; background: var(--bg-secondary); padding: 8px; border-radius: 8px; margin-bottom: 1rem;' },
    });

    topKeys.forEach((key) => {
      const isActive = activeTopKey === key;
      const btn = createElement('button', {
        className: `top-key-btn ${isActive ? 'active' : ''}`,
        attributes: {
          style: `padding: 8px 16px; border-radius: 6px; font-size: 0.85rem; font-family: monospace; font-weight: 700; border: none; cursor: pointer; color: ${
            isActive ? '#fff' : 'var(--color-text-muted)'
          }; background: ${isActive ? 'var(--color-primary)' : 'transparent'};`,
        },
        text: `📁 ${key}`,
      });

      btn.addEventListener('click', () => {
        activeTopKey = key;
        activeSubKey = '';
        renderUI();
      });

      topNav.appendChild(btn);
    });

    container.appendChild(topNav);

    // Body hiển thị nội dung của activeTopKey
    const bodyCard = createElement('div', {
      className: 'top-key-body',
      attributes: { style: 'background: var(--bg-secondary); padding: 1.25rem; border-radius: 8px; border: var(--border-glass);' },
    });

    const currentTopVal = jsonObj[activeTopKey];

    if (currentTopVal && typeof currentTopVal === 'object' && !Array.isArray(currentTopVal)) {
      renderObjectGroup(bodyCard, jsonObj, activeTopKey, currentTopVal);
    } else if (Array.isArray(currentTopVal)) {
      renderArrayGroup(bodyCard, jsonObj, activeTopKey, currentTopVal);
    } else {
      renderPrimitiveField(bodyCard, jsonObj, activeTopKey, currentTopVal);
    }

    container.appendChild(bodyCard);
  };

  const renderObjectGroup = (parentEl, rootObj, basePath, objVal) => {
    const keys = Object.keys(objVal);
    const primitives = {};
    const nestedGroups = {};

    keys.forEach((k) => {
      const val = objVal[k];
      if (val !== null && typeof val === 'object') {
        nestedGroups[k] = val;
      } else {
        primitives[k] = val;
      }
    });

    const groupKeys = Object.keys(nestedGroups);
    if (!activeSubKey && groupKeys.length > 0) {
      activeSubKey = groupKeys[0];
    }

    // 1. Render các trường primitive thuộc object này
    const primKeys = Object.keys(primitives);
    if (primKeys.length > 0) {
      const primHeader = createElement('h5', {
        attributes: { style: 'font-family: monospace; color: var(--color-accent); margin-bottom: 0.75rem; font-size: 0.9rem;' },
        text: `🔹 ${basePath} (properties):`,
      });
      parentEl.appendChild(primHeader);

      const primGrid = createElement('div', {
        attributes: { style: 'display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.75rem; margin-bottom: 1.5rem;' },
      });

      primKeys.forEach((k) => {
        const fullPath = `${basePath}.${k}`;
        const val = primitives[k];
        primGrid.appendChild(createInputField(rootObj, fullPath, k, val));
      });

      parentEl.appendChild(primGrid);
    }

    // 2. Render Sub-Nav Tab Cấp 2 cho các nhóm con (Sub-Groups)
    if (groupKeys.length > 0) {
      const subNavHeader = createElement('div', {
        attributes: { style: 'font-size: 0.8rem; color: var(--color-text-dim); margin-bottom: 0.5rem;' },
        text: '📂 Các nhóm con (sub-groups):',
      });
      parentEl.appendChild(subNavHeader);

      const subNav = createElement('div', {
        className: 'sub-key-nav',
        attributes: { style: 'display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1rem; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 8px;' },
      });

      groupKeys.forEach((k) => {
        const isActive = activeSubKey === k;
        const btn = createElement('button', {
          className: `sub-key-btn ${isActive ? 'active' : ''}`,
          attributes: {
            style: `padding: 6px 12px; font-size: 0.8rem; font-family: monospace; font-weight: 600; border: none; cursor: pointer; color: ${
              isActive ? 'var(--color-accent)' : 'var(--color-text-dim)'
            }; border-bottom: 2px solid ${isActive ? 'var(--color-accent)' : 'transparent'}; background: transparent;`,
          },
          text: `📁 ${k}`,
        });

        btn.addEventListener('click', () => {
          activeSubKey = k;
          renderUI();
        });

        subNav.appendChild(btn);
      });

      parentEl.appendChild(subNav);

      // Render nội dung của activeSubKey
      const subContent = createElement('div', {
        attributes: { style: 'background: var(--bg-tertiary); padding: 1rem; border-radius: 6px; border: var(--border-glass);' },
      });

      const subPath = `${basePath}.${activeSubKey}`;
      const subVal = nestedGroups[activeSubKey];

      if (Array.isArray(subVal)) {
        renderArrayGroup(subContent, rootObj, subPath, subVal);
      } else if (subVal && typeof subVal === 'object') {
        renderObjectGroup(subContent, rootObj, subPath, subVal);
      } else {
        renderPrimitiveField(subContent, rootObj, subPath, subVal);
      }

      parentEl.appendChild(subContent);
    }
  };

  const renderArrayGroup = (parentEl, rootObj, basePath, arrVal) => {
    const isArrayOfObjects = arrVal.length > 0 && typeof arrVal[0] === 'object' && arrVal[0] !== null;

    if (isArrayOfObjects) {
      // Render bảng dữ liệu cho mảng các object (VD: units, unlock_gates)
      const headers = Object.keys(arrVal[0]);
      const tableWrapper = createElement('div', {
        attributes: { style: 'overflow-x: auto; max-height: 350px; overflow-y: auto;' },
      });

      let ths = headers.map((h) => `<th style="padding: 8px; font-family: monospace;">${h}</th>`).join('');
      let rows = arrVal
        .map((item, rowIdx) => {
          let tds = headers
            .map((h) => {
              const val = item[h];
              const inputType = typeof val === 'number' ? 'number' : 'text';
              const stepAttr = typeof val === 'number' && !Number.isInteger(val) ? 'step="any"' : '';
              return `<td style="padding: 6px;"><input type="${inputType}" ${stepAttr} class="table-arr-input" data-row="${rowIdx}" data-field="${h}" value="${val}" style="width: 75px; padding: 4px; border-radius: 4px; background: var(--bg-primary); border: var(--border-glass); color: #fff; font-family: monospace; font-size: 0.8rem;" /></td>`;
            })
            .join('');
          return `<tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 6px; font-family: monospace; color: var(--color-accent); font-weight: 700;">[${rowIdx}]</td>${tds}</tr>`;
        })
        .join('');

      tableWrapper.innerHTML = `
        <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.85rem;">
          <thead>
            <tr style="background: var(--bg-tertiary); border-bottom: 2px solid var(--color-accent);">
              <th style="padding: 8px;">Index</th>
              ${ths}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;

      tableWrapper.querySelectorAll('.table-arr-input').forEach((input) => {
        input.addEventListener('change', () => {
          const row = parseInt(input.getAttribute('data-row'));
          const field = input.getAttribute('data-field');
          const origVal = arrVal[row][field];
          let updatedVal = input.value;
          if (typeof origVal === 'number') {
            updatedVal = parseFloat(input.value) || 0;
          }
          arrVal[row][field] = updatedVal;
          setNestedValue(rootObj, basePath, arrVal);
          if (onChange) onChange();
        });
      });

      parentEl.appendChild(tableWrapper);
    } else {
      // Render mảng số hoặc mảng đơn (VD: worker_upgrade_costs, chapter_rewards)
      const isArrayOfArrays = arrVal.length > 0 && Array.isArray(arrVal[0]);

      if (isArrayOfArrays) {
        // Mảng 2 chiều (VD: chapter_rewards)
        arrVal.forEach((subArr, subIdx) => {
          const card = createElement('div', {
            attributes: { style: 'margin-bottom: 1rem; background: var(--bg-tertiary); padding: 0.75rem; border-radius: 6px;' },
          });
          card.innerHTML = `<div style="font-family: monospace; font-size: 0.85rem; color: var(--color-accent); margin-bottom: 0.5rem;">[${subIdx}] (${subArr.length} elements):</div>`;
          const subPath = `${basePath}.${subIdx}`;
          card.appendChild(renderSimpleArrayGrid(rootObj, subPath, subArr));
          parentEl.appendChild(card);
        });
      } else {
        parentEl.appendChild(renderSimpleArrayGrid(rootObj, basePath, arrVal));
      }
    }
  };

  const renderSimpleArrayGrid = (rootObj, basePath, arr) => {
    const grid = createElement('div', {
      attributes: { style: 'display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 0.5rem; max-height: 250px; overflow-y: auto; padding: 0.5rem; background: var(--bg-primary); border-radius: 6px;' },
    });

    arr.forEach((val, idx) => {
      const itemEl = createElement('div', { attributes: { style: 'font-size: 0.75rem;' } });
      const inputType = typeof val === 'number' ? 'number' : 'text';
      itemEl.innerHTML = `
        <span style="color: var(--color-text-dim); font-family: monospace;">[${idx}]</span>
        <input type="${inputType}" class="simple-arr-input" data-idx="${idx}" value="${val}" style="padding: 4px; border-radius: 4px; background: var(--bg-tertiary); border: var(--border-glass); color: #fff; width: 100%; font-family: monospace;" />
      `;

      const input = itemEl.querySelector('.simple-arr-input');
      input.addEventListener('change', () => {
        const itemIdx = parseInt(input.getAttribute('data-idx'));
        arr[itemIdx] = typeof val === 'number' ? parseFloat(input.value) || 0 : input.value;
        setNestedValue(rootObj, basePath, arr);
        if (onChange) onChange();
      });

      grid.appendChild(itemEl);
    });

    return grid;
  };

  const createInputField = (rootObj, fullPath, keyLabel, val) => {
    const card = createElement('div', {
      attributes: { style: 'background: var(--bg-tertiary); padding: 0.75rem; border-radius: 6px; border: var(--border-glass);' },
    });

    if (typeof val === 'boolean') {
      card.innerHTML = `
        <label style="display: flex; justify-content: space-between; align-items: center; cursor: pointer; font-size: 0.85rem; font-family: monospace;">
          <span>${keyLabel}:</span>
          <input type="checkbox" class="field-input" ${val ? 'checked' : ''} style="width: 18px; height: 18px;" />
        </label>
      `;
    } else {
      const inputType = typeof val === 'number' ? 'number' : 'text';
      const stepAttr = typeof val === 'number' && !Number.isInteger(val) ? 'step="any"' : '';
      card.innerHTML = `
        <label style="display: block; font-size: 0.85rem; font-family: monospace; color: var(--color-text-muted); margin-bottom: 0.25rem;">${keyLabel}:</label>
        <input type="${inputType}" ${stepAttr} class="field-input" value="${val}" style="width: 100%; padding: 6px 10px; border-radius: 4px; background: var(--bg-primary); border: var(--border-glass); color: #fff; font-family: monospace;" />
      `;
    }

    const input = card.querySelector('.field-input');
    input.addEventListener('change', () => {
      let updatedVal = val;
      if (typeof val === 'boolean') {
        updatedVal = input.checked;
      } else if (typeof val === 'number') {
        updatedVal = parseFloat(input.value) || 0;
      } else {
        updatedVal = input.value;
      }
      setNestedValue(rootObj, fullPath, updatedVal);
      if (onChange) onChange();
    });

    return card;
  };

  const renderPrimitiveField = (parentEl, rootObj, path, val) => {
    parentEl.appendChild(createInputField(rootObj, path, path, val));
  };

  renderUI();
  return container;
};
